'use client'

import React from 'react'

import { type LucideIcon } from 'lucide-react'
import { twMerge } from 'tailwind-merge'

// ============================================================================
// Types
// ============================================================================

interface DataBlockV2Props {
  children: React.ReactNode
  className?: string
  highlight?: boolean
}

interface DataBlockIconProps {
  icon: LucideIcon
  color?: 'primary' | 'accent' | 'success' | 'warning' | 'info' | 'neutral'
  className?: string
}

interface DataBlockLabelProps {
  children: React.ReactNode
  className?: string
}

interface DataBlockValueProps {
  children: React.ReactNode
  className?: string
  title?: string
}

interface DataBlockSubValueProps {
  children: React.ReactNode
  className?: string
}

interface DataBlockHeaderProps {
  children: React.ReactNode
  className?: string
}

// ============================================================================
// Color Mapping
// ============================================================================

const iconColorClasses: Record<
  NonNullable<DataBlockIconProps['color']>,
  string
> = {
  primary: 'text-accent-600 dark:text-accent-400',
  accent: 'text-accent-600 dark:text-accent-400',
  success: 'text-green-600 dark:text-green-400',
  warning: 'text-amber-500 dark:text-amber-400',
  info: 'text-blue-600 dark:text-blue-400',
  neutral: 'text-primary-500 dark:text-primary-400',
}

// ============================================================================
// Sub-Components
// ============================================================================

const Icon: React.FC<DataBlockIconProps> = ({
  icon: IconComponent,
  color = 'primary',
  className,
}) => {
  return (
    <IconComponent
      className={twMerge(
        'h-3 w-3 sm:h-3.5 sm:w-3.5',
        iconColorClasses[color],
        className,
      )}
    />
  )
}
Icon.displayName = 'DataBlockV2.Icon'

const Label: React.FC<DataBlockLabelProps> = ({ children, className }) => {
  return (
    <span
      className={twMerge(
        'text-xs font-semibold uppercase leading-4 tracking-wide text-primary-500 dark:text-primary-400 sm:text-sm sm:leading-normal sm:tracking-wider',
        className,
      )}
    >
      {children}
    </span>
  )
}
Label.displayName = 'DataBlockV2.Label'

const Value: React.FC<DataBlockValueProps> = ({
  children,
  className,
  title,
}) => {
  // Auto-generate title from children if it's a string
  const autoTitle =
    title ?? (typeof children === 'string' ? children : undefined)

  return (
    <div
      className={twMerge(
        'text-sm font-bold leading-4 text-primary-900 dark:text-primary-100 sm:text-base sm:leading-tight',
        autoTitle,
        className,
      )}
      title={autoTitle}
    >
      {children}
    </div>
  )
}
Value.displayName = 'DataBlockV2.Value'

const SubValue: React.FC<DataBlockSubValueProps> = ({
  children,
  className,
}) => {
  return (
    <div
      className={twMerge(
        'mt-0.5 text-xs font-medium leading-4 text-primary-500 dark:text-primary-400 sm:text-sm sm:leading-normal',
        className,
      )}
    >
      {children}
    </div>
  )
}
SubValue.displayName = 'DataBlockV2.SubValue'

const Header: React.FC<DataBlockHeaderProps> = ({ children, className }) => {
  return (
    <div
      className={twMerge(
        'mb-1 flex items-center gap-1 sm:mb-2 sm:gap-1.5',
        className,
      )}
    >
      {children}
    </div>
  )
}
Header.displayName = 'DataBlockV2.Header'

// ============================================================================
// Main Component
// ============================================================================

/**
 * DataBlockV2 - Compound component for displaying key stats/metrics
 *
 * A card-like component for showing labeled values with optional icons.
 * Used in the data grid section of solicitation detail pages.
 *
 * @example
 * ```tsx
 * <DataBlockV2>
 *   <DataBlockV2.Header>
 *     <DataBlockV2.Icon icon={Building2} color="info" />
 *     <DataBlockV2.Label>Agency</DataBlockV2.Label>
 *   </DataBlockV2.Header>
 *   <DataBlockV2.Value>DOD</DataBlockV2.Value>
 *   <DataBlockV2.SubValue>SOCOM</DataBlockV2.SubValue>
 * </DataBlockV2>
 * ```
 */
const DataBlockV2: React.FC<DataBlockV2Props> & {
  Header: typeof Header
  Icon: typeof Icon
  Label: typeof Label
  Value: typeof Value
  SubValue: typeof SubValue
} = ({ children, className, highlight = false }) => {
  return (
    <div
      className={twMerge(
        'flex flex-col rounded-md border bg-white p-2.5 shadow-sm transition-all dark:bg-primary-900 sm:rounded-lg sm:p-3.5',
        highlight
          ? 'border-accent-400 ring-1 ring-accent-400 shadow-accent-100 dark:shadow-accent-900/20'
          : 'border-primary-200 hover:shadow-md dark:border-primary-700',
        className,
      )}
    >
      {children}
    </div>
  )
}

DataBlockV2.displayName = 'DataBlockV2'
DataBlockV2.Header = Header
DataBlockV2.Icon = Icon
DataBlockV2.Label = Label
DataBlockV2.Value = Value
DataBlockV2.SubValue = SubValue

// ============================================================================
// Skeleton Sub-Component
// ============================================================================

interface DataBlockSkeletonProps {
  /** Whether to show the subvalue skeleton */
  showSubValue?: boolean
  className?: string
}

/**
 * DataBlockV2.Skeleton - Loading skeleton matching DataBlock dimensions
 *
 * @example
 * ```tsx
 * <DataBlockV2.Skeleton />
 * <DataBlockV2.Skeleton showSubValue />
 * ```
 */
const DataBlockSkeleton: React.FC<DataBlockSkeletonProps> = ({
  showSubValue = false,
  className,
}) => {
  return (
    <div
      className={twMerge(
        'flex flex-col rounded-lg border border-primary-200 bg-white p-3.5 shadow-sm dark:border-primary-700 dark:bg-primary-900',
        className,
      )}
    >
      {/* Header: Icon + Label skeleton */}
      <div className="mb-2 flex items-center gap-1.5">
        <div className="h-3.5 w-3.5 animate-pulse rounded bg-primary-200 dark:bg-primary-700" />
        <div className="h-2.5 w-16 animate-pulse rounded bg-primary-200 dark:bg-primary-700" />
      </div>

      {/* Value skeleton - text-base is ~16px line height, so h-6 (24px) accounts for font metrics */}
      <div className="h-6 w-full max-w-[120px] animate-pulse rounded bg-primary-200 dark:bg-primary-700" />

      {/* SubValue skeleton (optional) */}
      {showSubValue && (
        <div className="mt-1 h-3.5 w-20 animate-pulse rounded bg-primary-200 dark:bg-primary-700" />
      )}
    </div>
  )
}

DataBlockSkeleton.displayName = 'DataBlockV2.Skeleton'

// Attach skeleton to DataBlockV2
const DataBlockV2WithSkeleton = DataBlockV2 as typeof DataBlockV2 & {
  Skeleton: typeof DataBlockSkeleton
}
DataBlockV2WithSkeleton.Skeleton = DataBlockSkeleton

export { DataBlockV2WithSkeleton as DataBlockV2 }
export type {
  DataBlockV2Props,
  DataBlockHeaderProps,
  DataBlockIconProps,
  DataBlockLabelProps,
  DataBlockValueProps,
  DataBlockSubValueProps,
  DataBlockSkeletonProps,
}
